import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React from 'react'
import Button from '@/components/buttons/Button/Button'
import LicensePlate from '@/components/vehicle/LicensePlate/LicensePlate'
import useLocalizedPath from '@/utils/hooks/useLocalizedPath'
import { PLATE_SEARCH_FLAG } from '@/utils/hooks/usePlateSearchArrival'
import styles from './SeoVehicleSearch.module.scss'

/** Keyed by route, so a change to a Swedish slug in pathMapping.json carries through. */
const CATEGORIES = {
  wheels: { label: 'search.wheel', acceptsPlate: true },
  rims: { label: 'search.rim', acceptsPlate: true },
  // TireSearch has no registration-number input, so tyres can only be linked to.
  tyres: { label: 'search.tire', acceptsPlate: false },
}

type Props = {
  /** Route key of the category search this page feeds. */
  category: keyof typeof CATEGORIES
}

/**
 * The route from an SEO page into the category search.
 *
 * Most of the copy promises that entering a registration number will show what fits the
 * reader's car, so where the search can honour that the plate is entered here. It travels
 * in the store rather than the URL — the same handoff the start page uses, which keeps the
 * plate out of browser history and referrer headers.
 */
const SeoVehicleSearch = ({ category }: Props) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const getLocalizedPath = useLocalizedPath()

  const { label, acceptsPlate } = CATEGORIES[category]
  const href = getLocalizedPath(category)

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    router.push(`${href}?${PLATE_SEARCH_FLAG}=true`)
  }

  if (!acceptsPlate) {
    return (
      <p className={styles.component}>
        <Link href={href} className={styles.link}>
          {t(label)} →
        </Link>
      </p>
    )
  }

  return (
    <form className={styles.component} onSubmit={onSubmit}>
      <LicensePlate animated />
      <Button label={t('search.searchButton')} rounded medium arrow type="submit" />
    </form>
  )
}

export default SeoVehicleSearch
